/**
 * The rate card: what one unit of each metered thing costs, from when.
 *
 * Four cards. The rate rows, with the one in force marked and history a
 * toggle away; a declared pair nobody has priced is listed above the rows
 * and its Price link prefills the form, because a missing rate looks exactly
 * like something cheap. A rate prices what the vendor did not report: a pair
 * whose type carries receipts is labeled as the fallback, with how often the
 * month needed it. The reconciliation, which puts the month's receipts
 * beside what the rate card would have charged for the same events, so a
 * stale fallback rate is visible. The exchange rate, fetched daily and
 * enterable by hand, which the pricing pass freezes onto each reported line.
 * And the subscriptions, which are plans bought per period and never priced
 * per run.
 *
 * The Add rate form's type, unit, and resource fields offer what the
 * platform knows (Feature 63): every declared or seen type, the chosen
 * type's units, and its resources with `*` first, the declared models,
 * then strings only events carried, marked with their counts. Free text
 * stays on all three: the list is advisory.
 *
 * Nothing here is a secret, so nothing is behind the password step-up.
 */
import { ApiError } from '@optum/api-client/client';
import {
  addFxRate,
  addRateRow,
  addSubscription,
  getCostChoices,
  getFxRates,
  getRateCard,
  getReconciliation,
  listSubscriptions,
} from '@optum/api-client/endpoints/cost';
import type { CostPeriod } from '@optum/api-client/endpoints/cost';
import type { RateRow, TypeChoice, UnpricedPair } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import {
  absoluteDate,
  absoluteDateTime,
  perMillion,
  quantity,
  thb,
  thbExact,
} from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

import { Combobox, type ComboboxOption } from '../../ui/Combobox.js';
import { DataTable } from '../../ui/DataTable.js';
import { Field } from '../../ui/Field.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { Toggle } from '../../ui/Toggle.js';
import { Badge, Callout, Grid, Input, Typography } from '../../ui/finixt.js';
import { monthOf, monthRange } from './period.js';
import { RepriceCard } from './RepriceCard.js';

export interface ChoiceOption {
  value: string;
  /** Shown beside the value: a pricing mode, a count, or "seen only". */
  mark?: string;
}

/** A dropdown of known values that still takes any string, the way the rate card matches keys. */
function toComboboxOptions(options: ChoiceOption[]): ComboboxOption[] {
  return options.map((o) => ({
    value: o.value,
    label: o.value,
    ...(o.mark ? { meta: o.mark } : {}),
  }));
}

const useValue = (query: string): string => `Use “${query}”`;

/** Units priced per million: what a vendor quotes for tokens. Others are per unit. */
const PER_MILLION_UNITS = /tokens$|characters$/;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function message(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Request failed';
}

export function RateCardTab() {
  const [month, setMonth] = useState(() => monthOf(new Date()));
  const period = monthRange(month);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          alignSelf: 'flex-end',
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-muted)',
        }}
      >
        Month
        <input
          type="month"
          value={month}
          onChange={(e) => e.target.value && setMonth(e.target.value)}
          aria-label="Month"
          style={{
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
            paddingLeft: 7,
            paddingRight: 7,
            paddingTop: 5,
            paddingBottom: 5,
            fontSize: 'var(--text-body-md)',
            color: 'var(--text-strong)',
          }}
        />
      </label>
      <RatesCard period={period} />
      <ReconciliationCard period={period} />
      <FxCardSection />
      <SubscriptionsCard />
      <RepriceCard />
    </div>
  );
}

interface RateDraft {
  usage_type: string;
  resource: string;
  unit: string;
  currency: string;
  price: string;
  effective_from: string;
}

const EMPTY_DRAFT: RateDraft = {
  usage_type: '',
  resource: '*',
  unit: '',
  currency: 'THB',
  price: '',
  effective_from: todayIso(),
};

/** The currencies the form offers: THB and USD always, then every currency a row is quoted in. */
export function currencyOptions(rows: RateRow[]): ChoiceOption[] {
  const seen = new Set(['THB', 'USD', ...rows.map((r) => r.currency)]);
  return [...seen].map((value) => ({ value }));
}

/** The datalist options for the three key fields, from the choices route. */
export function choiceOptions(
  types: TypeChoice[],
  usageType: string,
  resource = '',
): { types: ChoiceOption[]; units: ChoiceOption[]; resources: ChoiceOption[] } {
  const chosen = types.find((t) => t.usage_type === usageType);
  // The units the vendor's list prices for the chosen model need no operator row.
  const vendorUnits = new Set(
    chosen?.resources.find((r) => r.resource === resource)?.vendor_units ?? [],
  );
  return {
    types: types.map((t) => ({ value: t.usage_type, mark: t.declared ? t.pricing : 'seen only' })),
    units: (chosen?.units ?? []).map((u) => ({
      value: u.unit,
      ...(vendorUnits.has(u.unit)
        ? { mark: 'vendor-priced' }
        : u.declared
          ? {}
          : { mark: 'seen only' }),
    })),
    resources: [
      { value: '*', mark: 'every resource of the type' },
      ...(chosen?.resources ?? []).map((r): ChoiceOption => {
        if (!r.declared) return { value: r.resource, mark: `seen only · ${r.events} events` };
        return r.events > 0
          ? { value: r.resource, mark: `${r.events} events` }
          : { value: r.resource };
      }),
    ],
  };
}

function RatesCard({ period }: { period: CostPeriod }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = qk.rateCard(period.start, period.end);
  const card = useQuery({ queryKey: key, queryFn: () => getRateCard(period) });
  const choices = useQuery({ queryKey: qk.costChoices(), queryFn: getCostChoices });
  const [currentOnly, setCurrentOnly] = useState(true);
  const [draft, setDraft] = useState<RateDraft>(EMPTY_DRAFT);
  const perMillionEntry = PER_MILLION_UNITS.test(draft.unit);

  const add = useMutation({
    mutationFn: () =>
      addRateRow({
        usage_type: draft.usage_type.trim(),
        resource: draft.resource.trim() || '*',
        unit: draft.unit.trim(),
        ...(perMillionEntry ? { price_per_million: draft.price } : { price_per_unit: draft.price }),
        effective_from: `${draft.effective_from}T00:00:00+00:00`,
        currency: draft.currency.trim().toUpperCase() || 'THB',
      }),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      toast.success('Rate added');
      setDraft({ ...EMPTY_DRAFT, effective_from: todayIso() });
    },
    onError: (error) => toast.error(message(error)),
  });

  if (card.isPending) return <LoadingBlock />;
  if (card.isError) return <ErrorBlock message={message(card.error)} />;
  const rows = currentOnly ? card.data.rows.filter((r) => r.current) : card.data.rows;
  const options = choiceOptions(
    choices.data?.types ?? [],
    draft.usage_type.trim(),
    draft.resource.trim(),
  );
  // An operator row whose key also has a vendor row is an override.
  const vendorKeys = new Set(
    card.data.rows
      .filter((r) => r.source === 'vendor')
      .map((r) => `${r.usage_type}|${r.resource}|${r.unit}`),
  );
  const currency = draft.currency.trim().toUpperCase() || 'THB';
  const priceLabel = perMillionEntry ? `${currency} per 1M` : `${currency} per unit`;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Rate card"
        subtitle="THB per unit, from an effective date onward; the next row for the same key closes the previous one. A rate prices what the vendor did not report."
        actions={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Toggle checked={currentOnly} onChange={setCurrentOnly} label="Current only" />
            <Typography variant="meta">Off shows every row's history</Typography>
          </div>
        }
      />
      {(card.data.unpriced.length > 0 || card.data.unpriced_without_list_price > 0) && (
        <Callout tone="warn">
          <p
            style={{
              marginBottom: 7,
              fontSize: 'var(--text-body-md)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--warn)',
            }}
          >
            {card.data.unpriced.length > 0 && (
              <>
                {card.data.unpriced.length} declared unit
                {card.data.unpriced.length === 1 ? '' : 's'} with no rate. Calls without a receipt
                are collected and reported unpriced; a pair marked fallback belongs to a type whose
                receipts cost the rest.{' '}
              </>
            )}
            {card.data.unpriced_without_list_price > 0 && (
              <>
                {card.data.unpriced_without_list_price} event
                {card.data.unpriced_without_list_price === 1 ? '' : 's'} this period arrived with no
                list price and {card.data.unpriced_without_list_price === 1 ? 'was' : 'were'} rated
                from the fallback rows.
              </>
            )}
          </p>
          <ul style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {card.data.unpriced.map((pair) => (
              <UnpricedChip
                key={`${pair.usage_type}|${pair.unit}`}
                pair={pair}
                onPrice={(p) =>
                  setDraft((d) => ({ ...d, usage_type: p.usage_type, unit: p.unit, resource: '*' }))
                }
              />
            ))}
          </ul>
        </Callout>
      )}
      {rows.length === 0 ? (
        <EmptyBlock>
          No rate rows yet. Add one below; a declared pair's Price link prefills the form.
        </EmptyBlock>
      ) : (
        <DataTable
          rows={rows}
          rowStyle={(row) => (row.current && !row.overridden ? undefined : { opacity: 0.6 })}
          columns={[
            { key: 'usage_type', label: 'Usage type', mono: true, strong: true },
            { key: 'resource', label: 'Resource', mono: true },
            { key: 'unit', label: 'Unit', mono: true },
            {
              key: 'per_million',
              label: 'Per 1M',
              align: 'right',
              mono: true,
              render: (row) =>
                row.currency === 'THB'
                  ? perMillion(row.price_per_unit)
                  : `${row.currency} ${perMillion(row.price_per_unit)}`,
            },
            {
              key: 'per_million_thb',
              label: 'THB per 1M',
              align: 'right',
              mono: true,
              render: (row) =>
                row.currency === 'THB'
                  ? ''
                  : row.price_per_million_thb === null
                    ? `no ${row.currency} row yet`
                    : thbExact(row.price_per_million_thb),
            },
            { key: 'price_per_unit', label: 'Stored per unit', align: 'right', mono: true },
            {
              key: 'effective_from',
              label: 'Effective from',
              mono: true,
              render: (row) => absoluteDate(row.effective_from),
            },
            {
              key: 'source',
              label: 'Source',
              wrap: true,
              render: (row) => (
                <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
                  {row.source === 'vendor' ? (
                    <>
                      <Badge tone="info">vendor</Badge>
                      <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                        seen {absoluteDate(row.effective_from)}
                      </span>
                      {row.overridden && <Badge tone="neutral">overridden</Badge>}
                    </>
                  ) : (
                    <>
                      <Badge tone="ok">operator</Badge>
                      {row.source === 'operator' &&
                        vendorKeys.has(`${row.usage_type}|${row.resource}|${row.unit}`) && (
                          <Badge tone="warn">override</Badge>
                        )}
                    </>
                  )}
                </span>
              ),
            },
            {
              key: 'pricing_model',
              label: 'Model',
              render: (row) => <Badge tone="neutral">{row.pricing_model}</Badge>,
            },
            {
              key: 'current',
              label: '',
              render: (row) =>
                row.current ? (
                  <Badge tone="ok">current</Badge>
                ) : (
                  <Badge tone="neutral">history</Badge>
                ),
            },
          ]}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
          padding: 16,
        }}
      >
        <Grid columns={2} gap={16}>
          <Field label="Usage type" required>
            <Combobox
              aria-label="Usage type"
              value={draft.usage_type}
              onChange={(usage_type) => setDraft({ ...draft, usage_type })}
              options={toComboboxOptions(options.types)}
              placeholder="openrouter.chat"
              clearable
              searchPlaceholder="Search or type a value…"
              onCreate={(usage_type) => setDraft({ ...draft, usage_type })}
              createLabel={useValue}
            />
          </Field>
          <Field label="Resource" hint="* prices every resource of the type">
            <Combobox
              aria-label="Resource"
              value={draft.resource}
              onChange={(resource) => setDraft({ ...draft, resource })}
              options={toComboboxOptions(options.resources)}
              clearable
              searchPlaceholder="Search or type a value…"
              onCreate={(resource) => setDraft({ ...draft, resource })}
              createLabel={useValue}
            />
          </Field>
          <Field label="Unit" required>
            <Combobox
              aria-label="Unit"
              value={draft.unit}
              onChange={(unit) => setDraft({ ...draft, unit })}
              options={toComboboxOptions(options.units)}
              placeholder="input_tokens"
              clearable
              searchPlaceholder="Search or type a value…"
              onCreate={(unit) => setDraft({ ...draft, unit })}
              createLabel={useValue}
            />
          </Field>
        </Grid>
        <Grid columns={2} gap={16}>
          <Field label="Currency" hint="As the vendor quotes; converted at each run's day">
            <Combobox
              aria-label="Currency"
              value={draft.currency}
              onChange={(c) => setDraft({ ...draft, currency: c })}
              options={toComboboxOptions(currencyOptions(card.data.rows))}
              clearable
              searchPlaceholder="Search or type a value…"
              onCreate={(c) => setDraft({ ...draft, currency: c })}
              createLabel={useValue}
            />
          </Field>
          <Field
            label={priceLabel}
            required
            hint={perMillionEntry ? 'Entered per million, stored per unit' : 'Stored as entered'}
          >
            <Input
              aria-label={priceLabel}
              inputMode="decimal"
              value={draft.price}
              onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              placeholder="14.00"
            />
          </Field>
          <Field label="Effective from" required>
            <Input
              type="date"
              aria-label="Effective from"
              value={draft.effective_from}
              onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
            />
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              type="submit"
              variant="primary"
              disabled={add.isPending || !draft.usage_type || !draft.unit || !draft.price}
            >
              Add rate
            </Button>
          </div>
        </Grid>
      </form>
    </section>
  );
}

function UnpricedChip({
  pair,
  onPrice,
}: {
  pair: UnpricedPair;
  onPrice: (p: UnpricedPair) => void;
}) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        borderRadius: 'var(--radius-control)',
        border: '1px solid var(--warn)',
        background: 'var(--surface-card)',
        paddingLeft: 7,
        paddingRight: 7,
        paddingTop: 5,
        paddingBottom: 5,
        fontSize: 'var(--text-meta-lg)',
      }}
    >
      <code className="mono">{pair.usage_type}</code>
      <span style={{ color: 'var(--text-muted)' }}>{pair.unit}</span>
      {pair.fallback && <Badge tone="ok">fallback</Badge>}
      {pair.rated_events > 0 && (
        <span style={{ color: 'var(--text-muted)' }}>{quantity(pair.rated_events)} rated</span>
      )}
      <button
        type="button"
        onClick={() => onPrice(pair)}
        style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-accent)' }}
      >
        Price
      </button>
    </li>
  );
}

function ReconciliationCard({ period }: { period: CostPeriod }) {
  const rows = useQuery({
    queryKey: qk.reconciliation(period.start, period.end),
    queryFn: () => getReconciliation(period),
  });
  if (rows.isPending) return <LoadingBlock />;
  if (rows.isError) return <ErrorBlock message={message(rows.error)} />;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Reconciliation"
        subtitle="The month's receipts per served model beside what the rate card would have charged for the same events. A large difference is a stale fallback rate, not an error; nothing here writes one."
      />
      {rows.data.length === 0 ? (
        <EmptyBlock>No receipts were recorded in this month.</EmptyBlock>
      ) : (
        <DataTable
          rows={rows.data}
          rowKey={(row) => `${row.usage_type}|${row.resource}`}
          columns={[
            { key: 'usage_type', label: 'Usage type', mono: true, strong: true },
            { key: 'resource', label: 'Resource', mono: true },
            {
              key: 'events',
              label: 'Events',
              align: 'right',
              mono: true,
              render: (row) => quantity(row.events),
            },
            {
              key: 'reported_total',
              label: 'Reported',
              align: 'right',
              mono: true,
              render: (row) => thb(row.reported_total),
            },
            {
              key: 'rated_estimate',
              label: 'Rate card would',
              align: 'right',
              mono: true,
              render: (row) =>
                row.rated_estimate === null ? 'unavailable' : thb(row.rated_estimate),
            },
            {
              key: 'difference',
              label: 'Difference',
              align: 'right',
              mono: true,
              render: (row) =>
                row.difference === null ? '—' : <Difference value={row.difference} />,
            },
            {
              key: 'no_rate_events',
              label: 'No row',
              align: 'right',
              mono: true,
              render: (row) => quantity(row.no_rate_events),
            },
          ]}
        />
      )}
    </section>
  );
}

/** Reported less rated, signed and colored: amber when the card undercharges, red when it over. */
function Difference({ value }: { value: string }) {
  const n = Number(value);
  const label = n > 0 ? `+${thb(n)}` : n < 0 ? `−${thb(-n)}` : thb(0);
  return (
    <span style={{ color: n > 0 ? 'var(--warn)' : n < 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
      {label}
    </span>
  );
}

function FxCardSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const rates = useQuery({ queryKey: qk.fxRates(), queryFn: getFxRates });
  const [base, setBase] = useState('USD');
  const [rate, setRate] = useState('');
  const [asOf, setAsOf] = useState(todayIso());
  const add = useMutation({
    mutationFn: () => addFxRate({ base: base.trim().toUpperCase() || 'USD', rate, as_of: asOf }),
    onSuccess: (data) => {
      qc.setQueryData(qk.fxRates(), data);
      toast.success('Exchange rate added');
      setRate('');
    },
    onError: (error) => toast.error(message(error)),
  });

  if (rates.isPending) return <LoadingBlock />;
  if (rates.isError) return <ErrorBlock message={message(rates.error)} />;
  const latest = rates.data.latest;
  const bases = [...new Set(['USD', ...latest.map((r) => r.base)])].map((value) => ({ value }));

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Exchange rates"
        subtitle="To THB, one row per currency in use, fetched daily from open.er-api.com. The pricing pass converts each receipt and each rate row quoted in another currency at the row of its day and freezes it on the line."
        actions={
          latest.length > 0 && (
            <span
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 11,
                fontSize: 'var(--text-body-md)',
              }}
            >
              {latest.map((row) => (
                <span key={row.base} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
                    className="mono"
                  >
                    {row.base}
                  </span>
                  <span style={{ fontWeight: 'var(--weight-semibold)' }} className="mono">
                    {row.rate}
                  </span>
                  <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                    as of {absoluteDate(row.as_of)}
                  </span>
                  {row.stale ? (
                    <Badge tone="warn">stale</Badge>
                  ) : (
                    <Badge tone="neutral">{row.source}</Badge>
                  )}
                </span>
              ))}
            </span>
          )
        }
      />
      {rates.data.history.length === 0 ? (
        <EmptyBlock>
          No rate yet. The daily fetch adds one per currency in use; enter one by hand below if the
          source is down.
        </EmptyBlock>
      ) : (
        <DataTable
          rows={rates.data.history}
          columns={[
            { key: 'base', label: 'Base', mono: true, strong: true },
            { key: 'rate', label: 'Rate', align: 'right', mono: true },
            {
              key: 'source',
              label: 'Source',
              render: (row) => (
                <Badge tone={row.source === 'operator' ? 'violet' : 'neutral'}>{row.source}</Badge>
              ),
            },
            { key: 'as_of', label: 'As of', mono: true, render: (row) => absoluteDate(row.as_of) },
            {
              key: 'effective_from',
              label: 'Effective from',
              mono: true,
              render: (row) => absoluteDateTime(row.effective_from),
            },
          ]}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
          padding: 16,
        }}
      >
        <Grid columns={2} gap={16}>
          <Field label="Base" required>
            <Combobox
              aria-label="Base"
              value={base}
              onChange={setBase}
              options={toComboboxOptions(bases)}
              clearable
              searchPlaceholder="Search or type a value…"
              onCreate={setBase}
              createLabel={useValue}
            />
          </Field>
          <Field label="Rate to THB" required>
            <Input
              aria-label="Rate to THB"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="35.90"
            />
          </Field>
          <Field label="As of" required>
            <Input
              type="date"
              aria-label="As of"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button type="submit" variant="secondary" disabled={add.isPending || !rate}>
              Add manual rate
            </Button>
          </div>
        </Grid>
      </form>
    </section>
  );
}

function SubscriptionsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const subs = useQuery({ queryKey: qk.subscriptions(), queryFn: listSubscriptions });
  const [draft, setDraft] = useState({
    provider: '',
    plan: '',
    period_start: '',
    period_end: '',
    amount: '',
  });
  const add = useMutation({
    mutationFn: () => addSubscription(draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions() });
      toast.success('Subscription recorded');
      setDraft({ provider: '', plan: '', period_start: '', period_end: '', amount: '' });
    },
    onError: (error) => toast.error(message(error)),
  });
  if (subs.isPending) return <LoadingBlock />;
  if (subs.isError) return <ErrorBlock message={message(subs.error)} />;
  const ready =
    draft.provider && draft.plan && draft.period_start && draft.period_end && draft.amount;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Subscriptions"
        subtitle="Plans bought per period. Never priced per run; a period rollup divides the plan by what was collected under it."
      />
      {subs.data.length === 0 ? (
        <EmptyBlock>No subscriptions recorded.</EmptyBlock>
      ) : (
        <DataTable
          rows={subs.data}
          columns={[
            { key: 'provider', label: 'Provider', mono: true, strong: true },
            { key: 'plan', label: 'Plan' },
            {
              key: 'period',
              label: 'Period',
              mono: true,
              render: (s) => `${absoluteDate(s.period_start)} → ${absoluteDate(s.period_end)}`,
            },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              mono: true,
              render: (s) => thb(s.amount),
            },
          ]}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
          padding: 16,
        }}
      >
        <Grid columns={2} gap={16}>
          <Field label="Provider" required>
            <Input
              aria-label="Provider"
              value={draft.provider}
              onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
              placeholder="tavily"
            />
          </Field>
          <Field label="Plan" required>
            <Input
              aria-label="Plan"
              value={draft.plan}
              onChange={(e) => setDraft({ ...draft, plan: e.target.value })}
              placeholder="pro"
            />
          </Field>
          <Field label="Amount (THB)" required>
            <Input
              aria-label="Amount (THB)"
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            />
          </Field>
        </Grid>
        <Grid columns={2} gap={16}>
          <Field label="Period start" required>
            <Input
              type="date"
              aria-label="Period start"
              value={draft.period_start}
              onChange={(e) => setDraft({ ...draft, period_start: e.target.value })}
            />
          </Field>
          <Field label="Period end" required hint="Exclusive">
            <Input
              type="date"
              aria-label="Period end"
              value={draft.period_end}
              onChange={(e) => setDraft({ ...draft, period_end: e.target.value })}
            />
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button type="submit" variant="secondary" disabled={add.isPending || !ready}>
              Record subscription
            </Button>
          </div>
        </Grid>
      </form>
    </section>
  );
}
